import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_teal-gold-sites/artifacts/qn91b6v2_Logo-transparent-try3.png";

export const Navbar = ({ navItems = [] }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
      window.scrollTo({ top: 0 });
    }
  };

  const defaultNavItems = [
    { label: "About", url: "/about" },
    { label: "News & Views", url: "/news" },
    { label: "Services", url: "/services" },
    { label: "The It's IT Process", url: "/process" },
    { label: "Contact", url: "/contact" },
  ];

  const items = navItems.length > 0 ? navItems : defaultNavItems;

  // Group items by parent_id to create hierarchy
  const parentItems = items.filter(item => !item.parent_id && item.is_active !== false);
  const getChildItems = (parentId) => items.filter(item => item.parent_id === parentId && item.is_active !== false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMobileDropdown(null);
  }, [location]);

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 w-full backdrop-blur-xl transition-all duration-300 ${
        isScrolled
          ? "bg-its-teal/95 border-b border-its-teal-light/20 shadow-lg"
          : "bg-its-teal/80 border-b border-its-teal-light/10"
      }`}
    >
      <div className="section-container">
        <nav className="flex items-center justify-between h-18 md:h-24">
          {/* Logo */}
          <Link to="/" onClick={handleLogoClick} className="flex items-center gap-3" data-testid="logo-link">
            <div className="flex flex-col items-start">
              <img
                src={LOGO_URL}
                alt="It's IT"
                className="h-12 md:h-16 w-auto"
                style={{ mixBlendMode: 'lighten' }}
              />
              <span className="text-[10px] md:text-xs text-its-gold-soft font-normal tracking-wide -mt-0.5 font-poppins">
                Vibe Coding – Smarter, Faster Web Solutions
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {parentItems.map((item, index) => {
              const children = getChildItems(item.id);
              const hasChildren = children.length > 0;

              if (hasChildren) {
                return (
                  <div
                    key={item.id || index}
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(item.id)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <button
                      data-testid={`nav-link-${item.url.replace("/", "")}`}
                      className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-its-gold ${
                        location.pathname.startsWith(item.url)
                          ? "text-its-gold"
                          : "text-its-offwhite"
                      }`}
                    >
                      {item.label}
                      <ChevronDown size={14} className={`transition-transform ${openDropdown === item.id ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {openDropdown === item.id && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 mt-2 min-w-[200px] bg-its-teal-dark/95 backdrop-blur-xl border border-its-teal-light/20 rounded-lg shadow-xl py-2 z-50"
                        >
                          <Link
                            to={item.url}
                            className="block px-4 py-2 text-sm text-its-offwhite hover:text-its-gold hover:bg-its-teal-light/10 transition-colors"
                          >
                            {item.label} Overview
                          </Link>
                          {children.map((child) => (
                            <Link
                              key={child.id}
                              to={child.url}
                              className={`block px-4 py-2 text-sm transition-colors hover:text-its-gold hover:bg-its-teal-light/10 ${
                                location.pathname === child.url ? "text-its-gold" : "text-its-offwhite"
                              }`}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }

              return (
                <Link
                  key={item.id || index}
                  to={item.url}
                  data-testid={`nav-link-${item.url.replace("/", "")}`}
                  className={`text-sm font-medium transition-colors hover:text-its-gold ${
                    location.pathname === item.url
                      ? "text-its-gold"
                      : "text-its-offwhite"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* CTA Button (Desktop) */}
          <div className="hidden lg:block">
            <Link
              to="/process"
              data-testid="cta-get-started"
              className="btn-primary"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-its-offwhite hover:text-its-gold transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-its-teal-dark border-t border-its-teal-light/20"
          >
            <div className="section-container py-4 flex flex-col gap-2">
              {parentItems.map((item, index) => {
                const children = getChildItems(item.id);
                const hasChildren = children.length > 0;

                if (hasChildren) {
                  return (
                    <div key={item.id || index}>
                      <button
                        onClick={() => setMobileDropdown(mobileDropdown === item.id ? null : item.id)}
                        className={`w-full flex items-center justify-between text-base font-medium py-2 transition-colors hover:text-its-gold ${
                          location.pathname.startsWith(item.url) ? "text-its-gold" : "text-its-offwhite"
                        }`}
                      >
                        {item.label}
                        <ChevronDown size={16} className={`transition-transform ${mobileDropdown === item.id ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {mobileDropdown === item.id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="pl-4 border-l border-its-teal-light/20 ml-2"
                          >
                            <Link
                              to={item.url}
                              className="block py-2 text-sm text-its-offwhite hover:text-its-gold"
                            >
                              {item.label} Overview
                            </Link>
                            {children.map((child) => (
                              <Link
                                key={child.id}
                                to={child.url}
                                className={`block py-2 text-sm transition-colors hover:text-its-gold ${
                                  location.pathname === child.url ? "text-its-gold" : "text-its-offwhite"
                                }`}
                              >
                                {child.label}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.id || index}
                    to={item.url}
                    data-testid={`mobile-nav-link-${item.url.replace("/", "")}`}
                    className={`block text-base font-medium py-2 transition-colors hover:text-its-gold ${
                      location.pathname === item.url ? "text-its-gold" : "text-its-offwhite"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <Link
                to="/process"
                data-testid="mobile-cta-get-started"
                className="btn-primary text-center mt-4"
              >
                Get Started
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
